import React, { useEffect, useState, useRef } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ConfigProvider, Spin, App as AntApp } from 'antd'
import { useTranslation } from 'react-i18next'
import zhCN from 'antd/locale/zh_CN'
import enUS from 'antd/locale/en_US'
import ptBR from 'antd/locale/pt_BR'
import { LoadingOutlined } from '@ant-design/icons'

// 状态管理
import { useAuthStore } from './stores/authStore'
import { useUIStore } from './stores/useUIStore'

// API配置
import { setErrorHandler } from './shared/api/client'

// 配置中心
import { getThemeConfig } from './config/theme.config'

// UI组件
import AppCard from './shared/components/ui/AppCard'

// 认证页面
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'

// 路由守卫
import {
  ProtectedRoute,
  AuthRequiredRoute,
  AdminRequiredRoute,
  PublicRoute
} from './components/auth/ProtectedRoute'

// 布局组件
import AuthLayout, { DashboardLayout, SimpleLayout, MinimalLayout } from './components/auth/AuthLayout'

// 应用模块
import DashboardPage from './modules/dashboard/Dashboard'
import ChatbotPage from './modules/chatbot/Chatbot'
import KnowledgeBasePage from './modules/knowledge/KnowledgeBase'
import OrderPage from './modules/order/Order'
import OrderDetailPage from './modules/order/detail/OrderDetailPage'
import CustomerHistoryPage from './modules/history/CustomerHistory'
import SettingsPage from './modules/settings/SystemSettings'
import ConnectorManagementPage from './modules/connectors/ConnectorManagement'
import AgentConfigPage from './modules/agent-config/AgentConfigPage'

// 用户管理模块
import UserManagementPage from './pages/users/UserManagement'
import CompanyManagementPage from './pages/companies/CompanyManagement'
import ProfilePage from './pages/profile/ProfilePage'

// 法律条款页面
import TermsPage from './pages/legal/TermsPage'
import PrivacyPage from './pages/legal/PrivacyPage'

// 坐席模块
import { HumanProtectedRoute, HumanPublicRoute } from './components/auth/HumanRoute'
const HumanLoginPage = React.lazy(() => import('./pages/human/HumanLoginPage'))
const HumanWorkspace = React.lazy(() => import('./pages/human/HumanWorkspace'))

const LoadingScreen: React.FC = () => (
  <div className="loading-screen">
    <AppCard className="loading-screen-card">
      <Spin
        indicator={<LoadingOutlined className="loading-screen-spinner" spin />}
        size="large"
      />
      <div className="loading-screen-title">Loading...</div>
      <div className="loading-screen-subtitle">&nbsp;</div>
    </AppCard>
  </div>
)

const NotFoundPage: React.FC = () => {
  const { t } = useTranslation('common')
  return (
    <div className="not-found-page">
      <h1 className="not-found-code">404</h1>
      <p className="not-found-message">{t('pageNotFound')}</p>
      <a href="/" className="not-found-link">{t('backToHome')}</a>
    </div>
  )
}

// 主应用路由
const AppRoutes: React.FC = () => {
  const location = useLocation()
  const { isAuthenticated } = useAuthStore()

  // 在路由变化时滚动到顶部
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <MinimalLayout>
            <LoginPage />
          </MinimalLayout>
        </PublicRoute>
      } />

      <Route path="/register" element={
        <PublicRoute>
          <MinimalLayout>
            <RegisterPage />
          </MinimalLayout>
        </PublicRoute>
      } />

      <Route path="/terms" element={
        <PublicRoute>
          <MinimalLayout>
            <TermsPage />
          </MinimalLayout>
        </PublicRoute>
      } />

      <Route path="/privacy" element={
        <PublicRoute>
          <MinimalLayout>
            <PrivacyPage />
          </MinimalLayout>
        </PublicRoute>
      } />

      {/* 重定向根路径 */}
      <Route path="/" element={
        <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />
      } />

      <Route path="/dashboard" element={
        <AuthRequiredRoute>
          <DashboardLayout>
            <DashboardPage />
          </DashboardLayout>
        </AuthRequiredRoute>
      } />

      <Route path="/chatbot" element={
        <AuthRequiredRoute>
          <DashboardLayout>
            <ChatbotPage />
          </DashboardLayout>
        </AuthRequiredRoute>
      } />

      <Route path="/knowledge" element={
        <AuthRequiredRoute>
          <DashboardLayout>
            <KnowledgeBasePage />
          </DashboardLayout>
        </AuthRequiredRoute>
      } />

      <Route path="/orders" element={
        <AuthRequiredRoute>
          <DashboardLayout>
            <OrderPage />
          </DashboardLayout>
        </AuthRequiredRoute>
      } />

      <Route path="/orders/:orderId" element={
        <AuthRequiredRoute>
          <DashboardLayout>
            <OrderDetailPage />
          </DashboardLayout>
        </AuthRequiredRoute>
      } />

      <Route path="/history" element={
        <AuthRequiredRoute>
          <DashboardLayout>
            <CustomerHistoryPage />
          </DashboardLayout>
        </AuthRequiredRoute>
      } />

      <Route path="/settings" element={
        <AuthRequiredRoute>
          <DashboardLayout>
            <SettingsPage />
          </DashboardLayout>
        </AuthRequiredRoute>
      } />

      <Route path="/connectors" element={
        <AdminRequiredRoute>
          <DashboardLayout>
            <ConnectorManagementPage />
          </DashboardLayout>
        </AdminRequiredRoute>
      } />

      <Route path="/agent-config" element={
        <AdminRequiredRoute>
          <DashboardLayout>
            <AgentConfigPage />
          </DashboardLayout>
        </AdminRequiredRoute>
      } />


      <Route path="/profile" element={
        <AuthRequiredRoute>
          <DashboardLayout>
            <ProfilePage />
          </DashboardLayout>
        </AuthRequiredRoute>
      } />

      <Route path="/humans" element={
        <AdminRequiredRoute>
          <DashboardLayout>
            <UserManagementPage />
          </DashboardLayout>
        </AdminRequiredRoute>
      } />

      <Route path="/company" element={
        <AdminRequiredRoute>
          <DashboardLayout>
            <CompanyManagementPage />
          </DashboardLayout>
        </AdminRequiredRoute>
      } />

      <Route path="/human/login" element={
        <HumanPublicRoute>
          <React.Suspense fallback={<LoadingScreen />}>
            <HumanLoginPage />
          </React.Suspense>
        </HumanPublicRoute>
      } />

      <Route path="/human/workspace" element={
        <HumanProtectedRoute>
          <React.Suspense fallback={<LoadingScreen />}>
            <HumanWorkspace />
          </React.Suspense>
        </HumanProtectedRoute>
      } />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

// 主应用组件
function App() {
  const { i18n } = useTranslation()
  const { company, isAuthenticated, refreshUserInfo } = useAuthStore()
  const { mode, brandTheme } = useUIStore()
  const [locale, setLocale] = useState(zhCN)

  // 应用初始化时，如果用户已登录，后台刷新用户信息（不阻塞渲染）
  const hasRefreshed = useRef(false)

  useEffect(() => {
    if (isAuthenticated && !hasRefreshed.current) {
      hasRefreshed.current = true
      refreshUserInfo().catch(() => {})
    }
    if (!isAuthenticated) {
      hasRefreshed.current = false
    }
  }, [isAuthenticated, refreshUserInfo])

  // 租户语言设置（不阻塞渲染）
  const hasHandledLanguage = useRef(false)
  useEffect(() => {
    if (hasHandledLanguage.current) return

    const tenantLanguage = company?.settings?.general?.language
    if (tenantLanguage && i18n.language !== tenantLanguage) {
      i18n.changeLanguage(tenantLanguage).then(() => {
        hasHandledLanguage.current = true
      })
    } else if (company) {
      hasHandledLanguage.current = true
    }
  }, [i18n, company])

  // Ant Design locale 跟随 i18n 语言
  useEffect(() => {
    const language = i18n.language
    document.documentElement.lang = language || 'en'

    if (language?.startsWith('zh')) {
      setLocale(zhCN)
    } else if (language?.startsWith('pt')) {
      setLocale(ptBR)
    } else {
      setLocale(enUS)
    }
  }, [i18n.language])

  const themeConfig = getThemeConfig(mode, brandTheme)

  const ErrorHandlerSetup = () => {
    const { message } = AntApp.useApp()

    useEffect(() => {
      setErrorHandler((errorMessage) => {
        message.error(errorMessage)
      })
    }, [message])

    return null
  }

  return (
    <ConfigProvider
      locale={locale}
      theme={themeConfig}
    >
      <AntApp>
        <ErrorHandlerSetup />
        <AppRoutes />
      </AntApp>
    </ConfigProvider>
  )
}

export default App